import React, { useEffect, useMemo, useState } from 'react';
import { Company, Lead, Task, User } from '../types';
import { api } from '../services/apiService';

interface TasksViewProps {
  user: User;
  company: Company;
}

const TasksView: React.FC<TasksViewProps> = ({ user, company }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const [leadId, setLeadId] = useState('');
  const [clientCompanyName, setClientCompanyName] = useState('');
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [status, setStatus] = useState<'todo' | 'done'>('todo');

  const loadData = async () => {
    setLoading(true);
    try {
      const [leadData, taskData] = await Promise.all([
        api.getLeads({ companyId: company.id, userId: user.id, role: user.role }),
        api.getTasks({ companyId: company.id })
      ]);
      setLeads(leadData || []);
      setTasks(taskData || []);
      await api.checkUpcomingTaskReminders(user.id);
    } catch (e) {
      console.error('Erreur chargement taches', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [company.id, user.id, user.role]);

  const filteredTasks = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter(t =>
      t.title.toLowerCase().includes(q) ||
      (t.comment || '').toLowerCase().includes(q) ||
      (t.clientCompanyName || '').toLowerCase().includes(q)
    );
  }, [tasks, searchTerm]);

  const [taskPage, setTaskPage] = useState(1);
  const taskPerPage = 25;
  const taskTotalPages = Math.ceil(filteredTasks.length / taskPerPage);
  const paginatedTasks = useMemo(() => {
    const start = (taskPage - 1) * taskPerPage;
    return filteredTasks.slice(start, start + taskPerPage);
  }, [filteredTasks, taskPage]);
  useEffect(() => { setTaskPage(1); }, [searchTerm]);

  const openCreateModal = () => {
    setEditingTask(null);
    setLeadId('');
    setClientCompanyName('');
    setTitle('');
    setComment('');
    setDueAt(new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16));
    setStatus('todo');
    setShowModal(true);
  };

  const openEditModal = (task: Task) => {
    setEditingTask(task);
    setLeadId(task.leadId || '');
    setClientCompanyName(task.clientCompanyName || '');
    setTitle(task.title);
    setComment(task.comment || '');
    setDueAt(new Date(task.dueAt).toISOString().slice(0, 16));
    setStatus(task.status);
    setShowModal(true);
  };

  const onSelectLead = (selectedLeadId: string) => {
    setLeadId(selectedLeadId);
    const lead = leads.find(l => String(l.id) === String(selectedLeadId));
    if (lead) setClientCompanyName(lead.clientCompanyName || '');
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !dueAt) {
      alert('Titre et échéance sont obligatoires.');
      return;
    }
    setIsSubmitting(true);
    try {
      if (editingTask) {
        const updated = await api.updateTask(editingTask.id, {
          leadId: leadId || undefined,
          clientCompanyName: clientCompanyName || undefined,
          title: title.trim(),
          comment: comment.trim(),
          dueAt: new Date(dueAt).toISOString(),
          status
        });
        setTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
      } else {
        const created = await api.createTask({
          companyId: company.id,
          leadId: leadId || undefined,
          clientCompanyName: clientCompanyName || undefined,
          title: title.trim(),
          comment: comment.trim(),
          dueAt: new Date(dueAt).toISOString()
        }, { id: user.id, name: user.name });
        setTasks(prev => [created, ...prev]);
      }
      await api.checkUpcomingTaskReminders(user.id);
      setShowModal(false);
    } catch (error) {
      console.error('Erreur sauvegarde tache', error);
      alert('Impossible de sauvegarder la tâche.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleDone = async (task: Task) => {
    try {
      const updated = await api.updateTask(task.id, { status: task.status === 'done' ? 'todo' : 'done' });
      setTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
    } catch (e) {
      console.error('Erreur validation tache', e);
      alert('Impossible de mettre à jour la tâche.');
    }
  };

  const [confirmDeleteTaskId, setConfirmDeleteTaskId] = useState<string | null>(null);
  const handleDeleteTask = async (taskId: string) => {
    try {
      await api.deleteTask(taskId);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      setConfirmDeleteTaskId(null);
    } catch (e) {
      console.error('Erreur suppression tache', e);
      alert('Impossible de supprimer la tâche.');
    }
  };

  return (
    <div className="orion-dashboard space-y-5 animate-in fade-in duration-500 h-[calc(100vh-100px)] flex flex-col">
      <div className="orion-toolbar flex flex-col md:flex-row justify-between items-center gap-3 p-1.5 rounded-xl border border-slate-200 shadow-sm flex-shrink-0">
        <input
          type="text"
          placeholder="Rechercher une tâche..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full md:w-96 bg-slate-50 border-none rounded-xl px-4 py-2.5 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700"
        />
        <button onClick={openCreateModal} className="orion-primary-btn text-white px-5 py-2.5 rounded-xl text-xs font-black flex items-center gap-2 transition-all shadow-lg">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
          NOUVELLE TÂCHE
        </button>
      </div>

      <div className="orion-table-shell flex-1 rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-x-auto custom-scrollbar flex-1">
          <table className="w-full text-left border-collapse">
            <thead className="orion-table-head sticky top-0 z-10">
              <tr className="text-slate-400 text-[10px] font-black uppercase tracking-widest border-b border-slate-100">
                <th className="px-6 py-5">Statut</th>
                <th className="px-6 py-5">Tâche</th>
                <th className="px-6 py-5">Client</th>
                <th className="px-6 py-5">Échéance</th>
                <th className="px-6 py-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {!loading && paginatedTasks.map(task => (
                <tr key={task.id} className="orion-table-row-hover group transition-colors">
                  <td className="px-6 py-4">
                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase border ${task.status === 'done' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                      {task.status === 'done' ? 'Validée' : 'À faire'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-bold text-slate-900">{task.title}</div>
                    <div className="text-xs text-slate-500">{task.comment || '-'}</div>
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-slate-700">{task.clientCompanyName || '-'}</td>
                  <td className="px-6 py-4 text-xs font-bold text-slate-500">{new Date(task.dueAt).toLocaleString('fr-FR')}</td>
                  <td className="px-6 py-4 text-right flex justify-end gap-2">
                    <button onClick={() => toggleDone(task)} className="px-3 py-2 rounded-lg text-xs font-black bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
                      {task.status === 'done' ? 'Réouvrir' : 'Valider'}
                    </button>
                    <button onClick={() => openEditModal(task)} className="px-3 py-2 rounded-lg text-xs font-black bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                      Modifier
                    </button>
                    {confirmDeleteTaskId === task.id ? (
                      <div className="flex gap-1">
                        <button onClick={() => handleDeleteTask(task.id)} className="px-2.5 py-2 rounded-lg text-xs font-black bg-rose-500 text-white hover:bg-rose-600">Oui</button>
                        <button onClick={() => setConfirmDeleteTaskId(null)} className="px-2.5 py-2 rounded-lg text-xs font-black bg-slate-200 text-slate-600 hover:bg-slate-300">Non</button>
                      </div>
                    ) : (
                      <button onClick={() => setConfirmDeleteTaskId(task.id)} className="px-3 py-2 rounded-lg text-xs font-black bg-rose-50 text-rose-600 hover:bg-rose-100" title="Supprimer">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && filteredTasks.length === 0 && (
            <div className="text-center py-12 text-slate-400 text-sm font-medium">Aucune tâche.</div>
          )}
        </div>
        {taskTotalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100">
            <span className="text-xs font-bold text-slate-400">{((taskPage - 1) * taskPerPage) + 1}-{Math.min(taskPage * taskPerPage, filteredTasks.length)} sur {filteredTasks.length}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setTaskPage(1)} disabled={taskPage === 1} className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30">{'<<'}</button>
              <button onClick={() => setTaskPage(p => Math.max(1, p - 1))} disabled={taskPage === 1} className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30">{'<'}</button>
              {Array.from({ length: Math.min(5, taskTotalPages) }, (_, i) => {
                let page: number;
                if (taskTotalPages <= 5) { page = i + 1; }
                else if (taskPage <= 3) { page = i + 1; }
                else if (taskPage >= taskTotalPages - 2) { page = taskTotalPages - 4 + i; }
                else { page = taskPage - 2 + i; }
                return <button key={page} onClick={() => setTaskPage(page)} className={`w-8 h-8 rounded-lg text-xs font-black ${taskPage === page ? 'bg-indigo-500 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>{page}</button>;
              })}
              <button onClick={() => setTaskPage(p => Math.min(taskTotalPages, p + 1))} disabled={taskPage === taskTotalPages} className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30">{'>'}</button>
              <button onClick={() => setTaskPage(taskTotalPages)} disabled={taskPage === taskTotalPages} className="px-2 py-1 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-30">{'>>'}</button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="orion-modal-backdrop fixed inset-0 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="orion-modal-panel w-full max-w-2xl rounded-[2rem] shadow-2xl p-8 border animate-in zoom-in-95">
            <h3 className="text-xl font-black mb-6 dark:text-white">{editingTask ? 'Modifier tâche' : 'Nouvelle tâche'}</h3>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Client / Prospect</label>
                  <select value={leadId} onChange={e => onSelectLead(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none">
                    <option value="">Aucun</option>
                    {leads.map(lead => (
                      <option key={lead.id} value={lead.id}>{(lead.clientCompanyName || 'Client')} - {lead.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Nom du client</label>
                  <input type="text" value={clientCompanyName} onChange={e => setClientCompanyName(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none" />
                </div>
              </div>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Titre de la tâche" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none" />
              <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Commentaire" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium outline-none h-24 resize-none" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Échéance</label>
                  <input type="datetime-local" value={dueAt} onChange={e => setDueAt(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none" />
                </div>
                {editingTask && (
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase mb-1 block">Statut</label>
                    <select value={status} onChange={e => setStatus(e.target.value as 'todo' | 'done')} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none">
                      <option value="todo">À faire</option>
                      <option value="done">Validée</option>
                    </select>
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-3 text-xs font-bold text-slate-400 hover:text-slate-600">Annuler</button>
                <button type="submit" disabled={isSubmitting} className="orion-modal-primary-btn flex-1 py-3 text-white rounded-xl text-xs font-bold shadow-lg disabled:opacity-50">
                  {isSubmitting ? 'Sauvegarde...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksView;
